// src/components/AdminDashboard/TournamentManagement.jsx
// Admin tool to create scheduled multi-table tournaments, manage lifecycle, and view standings.
import React, { useState, useEffect, useCallback } from 'react';
import { tournamentService } from '../../services/tournamentService';

const CURRENCIES = ['USD', 'INR', 'LRD', 'XOF', 'CFA', 'LOCAL'];
const SYMBOLS = { USD: '$', INR: 'Rs', LRD: 'L$', XOF: 'CFA', CFA: 'CFA', LOCAL: '' };

const CATEGORIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'friday_special', label: 'Friday Special (Weekend)' },
  { value: 'saturday_special', label: 'Saturday Special' },
  { value: 'sunday_special', label: 'Sunday Special' },
  { value: 'custom', label: 'Custom' },
];

const EMPTY_FORM = {
  category: 'daily', name: '', description: '', entryFee: '', currency: 'USD',
  maxPlayers: '100', minPlayers: 2, playersPerTable: 6, pointsTarget: 201,
  isFree: false, fixedPrize: '',
  scheduleType: 'one_time', recurrenceType: 'daily', recurrenceDay: 6, recurrenceDate: 1,
  tournamentStart: '', registrationStart: '', registrationEnd: '', visibilityDaysBefore: 1,
  totalRounds: 1, advancePerRound: [], prizeStructure: '1:50\n2:30\n3:10\n4-10:10', commissionRate: 10,
};

const TournamentManagement = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [standingsFor, setStandingsFor] = useState(null);
  const [standingsData, setStandingsData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [rentalForms, setRentalForms] = useState({});

  const load = useCallback(async () => {
    try { setLoading(true); const list = await tournamentService.listTournaments(); setTournaments(list || []); }
    catch (e) { setError(e.message || 'Failed to load tournaments'); }
    finally { setLoading(false); }
  }, []);

  const loadRequests = useCallback(async () => {
    try { const list = await tournamentService.listTournamentRequests(); setRequests(list || []); }
    catch (e) { console.error('Failed to load requests:', e.message); }
  }, []);

  useEffect(() => { load(); loadRequests(); }, [load]);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAdvanceChange = (index, value) => {
    setForm((prev) => {
      const arr = [...(prev.advancePerRound || [])];
      arr[index] = parseInt(value, 10) || 1;
      return { ...prev, advancePerRound: arr };
    });
  };

  const parsePrizeStructure = (text, isFree) => (text || '').split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
    const [r, val] = line.split(':');
    if (!r || val == null) return null;
    if (isFree) {
      if (r.includes('-')) { const [from, to] = r.split('-').map(Number); return { rankFrom: from, rankTo: to, amount: parseFloat(val) }; }
      return { rank: parseInt(r, 10), amount: parseFloat(val) };
    }
    if (r.includes('-')) { const [from, to] = r.split('-').map(Number); return { rankFrom: from, rankTo: to, percent: parseFloat(val) }; }
    return { rank: parseInt(r, 10), percent: parseFloat(val) };
  }).filter(Boolean);

  const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({
      category: t.category || 'custom',
      name: t.name || '',
      description: t.description || '',
      entryFee: t.entryFee != null ? String(t.entryFee) : '',
      currency: t.currency || 'USD',
      maxPlayers: String(t.maxPlayers || 100),
      minPlayers: t.minPlayers || 2,
      playersPerTable: t.playersPerTable || 6,
      pointsTarget: (t.settings && t.settings.pointsTarget) || 201,
      scheduleType: t.recurring ? 'recurring' : 'one_time',
      recurrenceType: t.recurrenceType || 'daily',
      recurrenceDay: t.recurrenceDay != null ? t.recurrenceDay : 6,
      recurrenceDate: t.recurrenceDate || 1,
      tournamentStart: toLocalInput(t.tournamentStart),
      registrationStart: toLocalInput(t.registrationStart),
      registrationEnd: toLocalInput(t.registrationEnd),
      visibilityDaysBefore: t.visibilityDaysBefore || 1,
      totalRounds: t.totalRounds || 1,
      advancePerRound: t.advancePerRound || [],
      prizeStructure: (t.prizeStructure || []).map((p) => (t.isFree === true || p.amount != null) ? (p.rankFrom != null ? `${p.rankFrom}-${p.rankTo}:${p.amount}` : `${p.rank}:${p.amount}`) : (p.rankFrom != null ? `${p.rankFrom}-${p.rankTo}:${p.percent}` : `${p.rank}:${p.percent}`)).join('\n'),
      commissionRate: t.commissionRate != null ? t.commissionRate : 10,
      isFree: t.isFree === true,
      fixedPrize: t.fixedPrize != null ? String(t.fixedPrize) : '',
    });
    window.scrollTo(0, 0);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true); setError(''); setMessage('');
    try {
      const advance = (form.advancePerRound || []).map((n) => parseInt(n, 10)).filter((n) => !isNaN(n) && n > 0);
      const payload = {
        category: form.category,
        name: form.category === 'custom' ? (form.name || '').trim() : undefined,
        description: (form.description || '').trim(),
        entryFee: parseFloat(form.entryFee) || 0,
        currency: form.currency,
        maxPlayers: parseInt(form.maxPlayers, 10) || 100,
        minPlayers: parseInt(form.minPlayers, 10) || 2,
        playersPerTable: parseInt(form.playersPerTable, 10) || 6,
        pointsTarget: parseInt(form.pointsTarget, 10) || 201,
        recurring: form.scheduleType === 'recurring',
        recurrenceType: form.recurrenceType,
        recurrenceDay: parseInt(form.recurrenceDay, 10),
        recurrenceDate: parseInt(form.recurrenceDate, 10),
        tournamentStart: form.tournamentStart ? new Date(form.tournamentStart).toISOString() : null,
        registrationStart: form.registrationStart ? new Date(form.registrationStart).toISOString() : null,
        registrationEnd: form.registrationEnd ? new Date(form.registrationEnd).toISOString() : null,
        visibilityDaysBefore: parseInt(form.visibilityDaysBefore, 10) || 1,
        totalRounds: parseInt(form.totalRounds, 10) || 1,
        advancePerRound: advance,
        prizeStructure: parsePrizeStructure(form.prizeStructure),
        commissionRate: parseFloat(form.commissionRate) || 10,
        isFree: form.isFree === true,
        fixedPrize: parseFloat(form.fixedPrize) || 0,
      };
      if (payload.isFree) {
        const fixedPrizes = parsePrizeStructure(form.prizeStructure, true);
        payload.prizeStructure = fixedPrizes;
        payload.fixedPrize = Math.round(fixedPrizes.reduce((s, p) => {
          if (p.rankFrom != null && p.rankTo != null) return s + (p.rankTo - p.rankFrom + 1) * (p.amount || 0);
          return s + (p.amount || 0);
        }, 0) * 100) / 100;
      }
      if (editingId) {
        await tournamentService.updateTournament(editingId, payload);
      } else {
        await tournamentService.createTournament(payload);
      }
      setMessage(editingId ? 'Tournament updated successfully' : 'Tournament created successfully');
      setEditingId(null);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) { setError(err.message || 'Failed to create tournament'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tournament?')) return;
    setError('');
    try { await tournamentService.deleteTournament(id); setMessage('Tournament deleted'); await load(); }
    catch (e) { setError(e.message || 'Failed to delete tournament'); }
  };

  const handleViewStandings = async (id) => {
    if (standingsFor === id) { setStandingsFor(null); setStandingsData(null); return; }
    try { const res = await tournamentService.getStandings(id); setStandingsData(res); setStandingsFor(id); }
    catch (e) { setError(e.message || 'Failed to load standings'); }
  };

  const handleToggleActive = async (t) => {
    setError('');
    try { await tournamentService.setTournamentActive(t.id, !t.isActive); setMessage(t.isActive ? 'Tournament deactivated' : 'Tournament activated'); await load(); }
    catch (e) { setError(e.message || 'Failed to toggle tournament'); }
  };

  const handleCloseRegistration = async (t) => {
    if (!window.confirm('Close registration and compute prizes (deduct commission + distribute)?')) return;
    setError('');
    try { const r = await tournamentService.closeRegistration(t.id); setMessage(`Registration closed. Prize pool: ${r.prizePool} (commission ${r.commission})`); await load(); }
    catch (e) { setError(e.message || 'Failed to close registration'); }
  };

  const handleRentalFieldChange = (requestId, field, value) => {
    setRentalForms((prev) => ({ ...prev, [requestId]: { ...(prev[requestId] || {}), [field]: value } }));
  };

  const handleCreateRental = async (requestId) => {
    setCreating(true); setError(''); setMessage('');
    try {
      const req = requests.find((r) => String(r.id) === String(requestId));
      const res = await tournamentService.createRentalTournament(requestId, { rentalFee: parseFloat(req?.rentalFee || 0) });
      const code = res && res.tournament ? res.tournament.tournamentCode : '';
      setMessage(`Private tournament created — code: ${code}`);
      await loadRequests();
      await load();
    } catch (err) { setError(err.message || 'Failed to create rental tournament'); }
    finally { setCreating(false); }
  };

  const handleDeclineRequest = async (requestId) => {
    const reason = window.prompt('Why is this request being rejected? (this will be shown to the organizer)');
    if (reason == null) return;
    try { await tournamentService.setTournamentRequestStatus(requestId, 'declined', reason.trim() || null); await loadRequests(); }
    catch (e) { setError(e.message || 'Failed to decline request'); }
  };

  const fmt = (amount, currency) => {
    const sym = SYMBOLS[currency] ?? '';
    const n = parseFloat(amount || 0);
    if (currency === 'XOF' || currency === 'CFA') return `${n.toFixed(0)} ${sym}`;
    return `${sym}${n.toFixed(2)}`;
  };

  return (
    <div style={{ padding: '8px' }}>
      <div className="section-card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h2>Create Tournament</h2></div>
        <form onSubmit={handleCreate} style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', alignItems: 'end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Category
            <select value={form.category} onChange={handleChange('category')}>{CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select>
          </label>
          {form.category === 'custom' && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Name
              <input value={form.name} onChange={handleChange('name')} placeholder="Custom tournament name" required />
            </label>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', gridColumn: '1 / -1' }}>
            <input type="checkbox" checked={form.isFree === true} onChange={(e) => setForm((p) => ({ ...p, isFree: e.target.checked, entryFee: e.target.checked ? '0' : p.entryFee, prizeStructure: e.target.checked ? '1:100\n2:50\n3:25' : '1:50\n2:30\n3:10\n4-10:10' }))} />
            <span>Free tournament — no entry fee, prize funded by super admin commission wallet</span>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Entry Fee (USD)
            <input type="number" min="0" value={form.entryFee} onChange={handleChange('entryFee')} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Currency
            <select value={form.currency} onChange={handleChange('currency')}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Max Players
            <input type="number" min="2" value={form.maxPlayers} onChange={handleChange('maxPlayers')} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Players Per Table (max 6)
            <input type="number" min="2" max="6" value={form.playersPerTable} onChange={handleChange('playersPerTable')} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Points Target
            <input type="number" value={form.pointsTarget} onChange={handleChange('pointsTarget')} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Commission (%)
            <input type="number" min="0" max="100" value={form.commissionRate} onChange={handleChange('commissionRate')} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Schedule
            <select value={form.scheduleType} onChange={handleChange('scheduleType')}>
              <option value="one_time">One time</option>
              <option value="recurring">Recurring</option>
            </select>
          </label>
          {form.scheduleType === 'recurring' && (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Recurrence
                <select value={form.recurrenceType} onChange={handleChange('recurrenceType')}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom</option>
                </select>
              </label>
              {form.recurrenceType === 'weekly' && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Weekday
                  <select value={form.recurrenceDay} onChange={handleChange('recurrenceDay')}>
                    <option value={0}>Sunday</option><option value={1}>Monday</option><option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option><option value={4}>Thursday</option><option value={5}>Friday</option><option value={6}>Saturday</option>
                  </select>
                </label>
              )}
              {form.recurrenceType === 'monthly' && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Day of Month
                  <input type="number" min="1" max="31" value={form.recurrenceDate} onChange={handleChange('recurrenceDate')} />
                </label>
              )}
            </>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Tournament Start
            <input type="datetime-local" value={form.tournamentStart} onChange={handleChange('tournamentStart')} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Registration Start
            <input type="datetime-local" value={form.registrationStart} onChange={handleChange('registrationStart')} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Registration End
            <input type="datetime-local" value={form.registrationEnd} onChange={handleChange('registrationEnd')} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Show on player page (days before)
            <input type="number" min="0" value={form.visibilityDaysBefore} onChange={handleChange('visibilityDaysBefore')} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>Total Rounds
            <input type="number" min="1" value={form.totalRounds} onChange={handleChange('totalRounds')} />
          </label>
          <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px' }}>Advance per round (players advancing from EACH table, one value per round; final round is ranked)
            {Array.from({ length: Math.max(0, (parseInt(form.totalRounds, 10) || 1) - 1) }, (_, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ minWidth: '70px' }}>Round {i + 1}:</span>
                <input type="number" min="1" max={parseInt(form.playersPerTable, 10) || 6} value={form.advancePerRound[i] || 3} onChange={(e) => handleAdvanceChange(i, e.target.value)} style={{ width: '80px' }} />
                <span style={{ opacity: 0.6 }}>advance</span>
              </div>
            ))}
          </label>

          <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px' }}>
            {form.isFree
              ? 'Prize Structure — fixed USD per rank (one per line, e.g. "1:500")'
              : 'Prize Structure (rank:percent or rankFrom-rankTo:percent, one per line)'}
            <textarea rows="5" value={form.prizeStructure} onChange={handleChange('prizeStructure')} style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }} />
          </label>

          <button type="submit" disabled={creating} style={{ gridColumn: '1 / -1', padding: '10px', cursor: 'pointer', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: '700' }}>
            {editingId ? 'Update Tournament' : 'Create Tournament'}
          </button>
        </form>
        {message && <div style={{ color: '#4caf50', marginTop: '10px' }}>{message}</div>}
        {error && <div style={{ color: '#f44336', marginTop: '10px' }}>{error}</div>}
      </div>

      <div className="section-card">
        <div className="card-header"><h2>Tournaments</h2></div>
        {loading ? <div style={{ padding: '16px' }}>Loading...</div> : tournaments.length === 0 ? <div style={{ padding: '16px' }}>No tournaments yet.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #333', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>Name</th>
                <th style={{ padding: '8px' }}>Category</th>
                <th style={{ padding: '8px' }}>Entry</th>
                <th style={{ padding: '8px' }}>Players</th>
                <th style={{ padding: '8px' }}>Prize Pool</th>
                <th style={{ padding: '8px' }}>Status</th>
                <th style={{ padding: '8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map((t) => (
                <React.Fragment key={t.id}>
                  <tr style={{ borderBottom: '1px solid #222' }}>
                    <td style={{ padding: '8px' }}>{t.name}</td>
                    <td style={{ padding: '8px' }}>{t.category || 'custom'}{t.recurring ? ' (recurring)' : ''}</td>
                    <td style={{ padding: '8px' }}>{fmt(t.entryFee, t.currency)}</td>
                    <td style={{ padding: '8px' }}>{t.currentPlayers}/{t.maxPlayers}</td>
                    <td style={{ padding: '8px' }}>{fmt(t.prizePool, t.currency)}</td>
                    <td style={{ padding: '8px' }}>{t.status}{t.isActive ? '' : ' (inactive)'}</td>
                    <td style={{ padding: '8px' }}>
                      <button onClick={() => handleToggleActive(t)} style={{ marginRight: '8px', cursor: 'pointer', background: t.isActive ? '#f44336' : '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px' }}>{t.isActive ? 'Deactivate' : 'Activate'}</button>
                      {t.status === 'registration' && (
                        <button onClick={() => handleCloseRegistration(t)} style={{ marginRight: '8px', cursor: 'pointer', background: '#ff9800', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px' }}>Close Reg</button>
                      )}
                      <button onClick={() => handleViewStandings(t.id)} style={{ marginRight: '8px', cursor: 'pointer' }}>{standingsFor === t.id ? 'Hide' : 'Standings'}</button>
                      {t.status !== 'active' && (
                        <><button onClick={() => handleEdit(t)} style={{ marginRight: '8px', cursor: 'pointer', color: '#2196f3' }}>Modify</button>
                      <button onClick={() => handleDelete(t.id)} style={{ cursor: 'pointer', color: '#f44336' }}>Delete</button></>
                      )}
                    </td>
                  </tr>
                  {standingsFor === t.id && standingsData && (
                    <tr>
                      <td colSpan={7} style={{ padding: '12px', background: '#111' }}>
                        <h4>Standings</h4>
                        {!standingsData.standings || standingsData.standings.length === 0 ? <div>No players yet.</div> : (
                          <table style={{ width: '100%' }}>
                            <thead><tr><th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>Player</th><th style={{ textAlign: 'left' }}>Score</th><th style={{ textAlign: 'left' }}>Prize</th></tr></thead>
                            <tbody>
                              {standingsData.standings.map((s, i) => (
                                <tr key={s.id}><td>{s.position || i + 1}</td><td>{s.username}</td><td>{s.score}</td><td>{fmt(s.prize, standingsData.tournament?.currency || 'USD')}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="section-card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><h2>Private Tournament Requests</h2></div>
        {requests.length === 0 ? (
          <div style={{ padding: '16px' }}>No requests yet. Players can request a private tournament from the Tournaments page.</div>
        ) : (
          requests.map((r) => (
            <div key={r.id} style={{ padding: '12px', borderBottom: '1px solid #222' }}>
              <div><strong>{r.username}</strong>{r.email ? ` (${r.email})` : ''}</div>
              <div style={{ margin: '6px 0', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px' }}>
                <span>Players: <strong>{r.maxPlayers || '—'}</strong></span>
                <span>Rental fee: <strong>${parseFloat(r.rentalFee || 0).toFixed(2)}</strong></span>
                <span>Advance/round: <strong>{(r.advancePerRound || []).join(', ') || '—'}</strong></span>
              </div>
              {r.prizeStructure && r.prizeStructure.length > 0 && (
                <div style={{ fontSize: '13px', margin: '4px 0' }}>
                  Prizes: {r.prizeStructure.map((p) => `${p.name || `Rank ${p.rank}`}: $${parseFloat(p.amount || 0).toFixed(2)}`).join(' · ')}
                </div>
              )}
              {r.message && <div style={{ opacity: 0.8, margin: '6px 0' }}>{r.message}</div>}
              <div style={{ fontSize: '12px', opacity: 0.6 }}>Status: {r.status} · {new Date(r.createdAt).toLocaleString()}</div>
              {r.status === 'declined' && r.rejectionReason && (
                <div style={{ fontSize: '12px', color: '#f44336', marginTop: '6px' }}>Reason: {r.rejectionReason}</div>
              )}
              {r.status === 'pending' && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px', alignItems: 'flex-end' }}>
                  <button onClick={() => handleCreateRental(r.id)} disabled={creating} style={{ cursor: 'pointer', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 10px' }}>✅ Approve & Create</button>
                  <button onClick={() => handleDeclineRequest(r.id)} style={{ cursor: 'pointer', background: '#f44336', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 10px' }}>Decline</button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TournamentManagement;